// Ask Atlas — the conversational assistant behind the ⌘K search bar.
// A resizable right-side panel (reuses window.Drawer) hosting a chat thread:
// text answers render as Markdown, record-list blocks render through the same
// DataTableWidget the grids use (name cells link to record pages via
// window.AtlasNav), and any block can be saved as a Saved View in one click
// (its spec carries compiled read-only SQL — the /views spec.sql path).
// "Create tasks" on a companies result block: one follow-up task per row,
// assigned to the current user, due date picked inline. Writes happen only on
// the user's explicit click — the model can only point at this button.
const CreateTasksControl = ({ block, activeUser, created, onCreate, phone }) => {
  const [open, setOpen] = React.useState(false);
  const [days, setDays] = React.useState(7);
  const [busy, setBusy] = React.useState(false);
  if (!activeUser || !activeUser.id) return null;
  if (created != null) {
    return (
      <window.Btn size="xs" variant="ghost" icon={<window.Icon.Check size={11} />} disabled>
        {created} task{created === 1 ? "" : "s"} created
      </window.Btn>
    );
  }
  if (!open) {
    return (
      <window.Btn
        size="xs"
        variant="outline"
        icon={<window.Icon.Check size={11} />}
        onClick={() => setOpen(true)}
        title="Create one follow-up task per company, assigned to you"
      >
        Create tasks
      </window.Btn>
    );
  }
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        ...(phone ? { flexWrap: "wrap", rowGap: 6 } : null),
      }}
    >
      <span style={{ fontSize: phone ? 13 : 11, color: "var(--text-3)", whiteSpace: "nowrap" }}>
        {block.rows.length} task{block.rows.length === 1 ? "" : "s"}, due
      </span>
      <window.Picker
        value={days}
        onChange={(v) => setDays(Number(v))}
        size={phone ? "md" : "sm"}
        placeholder="Due"
        options={[
          { value: 1, label: "tomorrow" },
          { value: 3, label: "in 3 days" },
          { value: 7, label: "in 1 week" },
          { value: 14, label: "in 2 weeks" },
        ]}
      />
      <window.Btn
        size="xs"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            await onCreate(block, days);
            setOpen(false);
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? "Creating…" : "Go"}
      </window.Btn>
      <window.Btn size="xs" variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
        Cancel
      </window.Btn>
    </span>
  );
};

// Answers reference records as markdown links the engine validated —
// [Name](#company/<uuid>) etc. Intercept those clicks and route them through
// window.AtlasNav (a real hash navigation would fight the app's routing).
const ENTITY_HREF_RE = /^#(company|contact|engagement|deal|buyer)\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
const ENTITY_NAV = { company: "goToCompany", contact: "goToContact", engagement: "goToDeal", deal: "goToDeal", buyer: "goToBuyer" };
const onEntityLinkClick = (e) => {
  const a = e.target && e.target.closest ? e.target.closest("a[href^='#']") : null;
  if (!a) return;
  const m = ENTITY_HREF_RE.exec(a.getAttribute("href") || "");
  if (!m) return;
  e.preventDefault();
  const nav = ENTITY_NAV[m[1].toLowerCase()];
  if (window.AtlasNav && window.AtlasNav[nav]) window.AtlasNav[nav](m[2], a.textContent || undefined);
};

// `phone`: larger reading text, a wrapping block header, and no "Save as
// view" (a saved view opens a desktop-only page). `readOnly` (a thread opened
// by its share link): the answer and its tables, no actions on them.
const AssistantMsg = ({ msg, onSaveView, savedViews, activeUser, tasksCreated, onCreateTasks, phone, readOnly }) => {
  const isUser = msg.role === "user";
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: isUser ? "flex-end" : "stretch",
        gap: 8,
      }}
    >
      <div
        style={{
          maxWidth: isUser ? "85%" : "100%",
          padding: isUser ? "8px 12px" : "2px 0",
          borderRadius: isUser ? 10 : 0,
          background: isUser ? "var(--accent-soft)" : "transparent",
          color: "var(--text)",
          fontSize: phone ? 15.5 : 13,
          lineHeight: 1.5,
          whiteSpace: isUser ? "pre-wrap" : undefined,
          ...(phone && isUser ? { overflowWrap: "anywhere" } : null),
        }}
      >
        {isUser ? (
          msg.content
        ) : (
          <div onClick={onEntityLinkClick}>
            <window.Markdown
              className={phone ? "md-reader" : undefined}
              style={phone ? { fontSize: 15, color: "var(--text)" } : undefined}
            >
              {msg.content}
            </window.Markdown>
          </div>
        )}
      </div>
      {(msg.blocks || []).map((b, i) =>
        b.type === "records" && b.rows && b.rows.length > 0 ? (
          <div
            key={b.id || i}
            style={{ border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "7px 10px",
                borderBottom: "1px solid var(--border)",
                background: "var(--surface-2)",
                ...(phone ? { flexWrap: "wrap", rowGap: 8, padding: "9px 12px" } : null),
              }}
            >
              <span
                style={{
                  fontSize: phone ? 14 : 12,
                  fontWeight: 600,
                  color: "var(--text)",
                  flex: phone ? "1 1 150px" : 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {b.title || "Results"}
              </span>
              <span style={{ fontSize: phone ? 13 : 11, color: "var(--text-3)", whiteSpace: "nowrap" }}>
                {b.row_count}
                {b.total_count > b.row_count ? ` of ${b.total_count}` : ""}
              </span>
              {b.entity === "companies" && !readOnly && (
                <CreateTasksControl
                  block={b}
                  activeUser={activeUser}
                  created={tasksCreated[b.id]}
                  onCreate={onCreateTasks}
                  phone={phone}
                />
              )}
              {!phone &&
                !readOnly &&
                b.spec &&
                b.spec.sql &&
                (savedViews[b.id] ? (
                  <window.Btn
                    size="xs"
                    variant="ghost"
                    icon={<window.Icon.Check size={11} />}
                    disabled
                  >
                    Saved
                  </window.Btn>
                ) : (
                  <window.Btn
                    size="xs"
                    variant="outline"
                    icon={<window.Icon.Table size={11} />}
                    onClick={() => onSaveView(b)}
                    title="Save these records as a shareable Saved View"
                  >
                    Save as view
                  </window.Btn>
                ))}
            </div>
            {/* Wide result tables scroll sideways inside the card — never the
                whole panel. */}
            <div style={{ overflowX: "auto", ...(phone ? { WebkitOverflowScrolling: "touch" } : null) }}>
              <window.DataTableWidget
                columns={b.table_columns || (b.columns || []).map((c) => ({ key: c, label: c.replace(/_/g, " ") }))}
                rows={b.rows}
                valueFormat="number"
                pageSize={10}
                entityHint={b.entity === "companies" ? "company" : b.entity === "buyers" ? "buyer" : null}
                hideIds
              />
            </div>
          </div>
        ) : null
      )}
    </div>
  );
};

// The link to one thread (#/ask/<id>). The native shell runs at
// capacitor://localhost, a dead link for whoever it's pasted to, so the link
// uses the public web address whenever it differs (same as a note's link).
const threadLink = (id) => {
  const hash =
    (window.AtlasRouter && window.AtlasRouter.buildHash({ view: "ask", askId: id })) || `/ask/${encodeURIComponent(id)}`;
  const { origin, pathname, search } = window.location;
  const pub = window.__ATLAS_PUBLIC_URL__;
  if (pub && pub !== origin) return `${pub.replace(/\/+$/, "")}/#${hash}`;
  return `${origin}${pathname}${search}#${hash}`;
};

const writeClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (_) {
    /* no secure context / denied — the textarea trick below still works */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.cssText = "position:fixed;top:-1000px;opacity:0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (_) {
    return false;
  }
};

// Only the signed-in member shares their own threads — never an admin, and
// never while browsing as someone else (whoami's team_member_id, not the
// active user). The server holds the same line.
const isMine = (ownerId) => {
  const me = (window.AtlasWhoami || {}).memberId;
  return !!ownerId && !!me && ownerId === me;
};

// Why a thread can't be shared (its share_blocked, set by the server the
// first time a turn reads one of these).
const SHARE_BLOCKED = {
  member_files: "Reads personal files — can't be shared",
  cim_architecture: "Reads a CIM's architecture — can't be shared",
  email_copilot: "Uses the Email Copilot — can't be shared",
};
const shareBlockedReason = (key) => SHARE_BLOCKED[key] || "This chat can't be shared";

// The server's sentence off an apiFetch error ("409 Conflict: {"detail": …}").
const apiDetail = (e) => {
  const m = /^\d{3}\b[^:]*:\s*([\s\S]*)$/.exec((e && e.message) || "");
  if (!m) return "";
  try {
    const d = JSON.parse(m[1]).detail;
    return typeof d === "string" ? d : "";
  } catch (_) {
    return "";
  }
};

// Copying a thread's link is what shares it (the server stamps shared_at). The
// clipboard write starts first, still inside the click — Safari refuses one
// that only starts after an await. Resolves to the new shared_at, or null.
const copyThreadLink = async (id) => {
  const url = threadLink(id);
  const [copied, shared] = await Promise.all([
    writeClipboard(url),
    window.AtlasApi.assistant.share(id).then(
      (r) => ({ at: (r && r.shared_at) || new Date().toISOString() }),
      (e) => ({ error: e })
    ),
  ]);
  if (shared.error) {
    const blocked = shared.error.status === 409;
    window.pushToast({
      type: "error",
      title: blocked ? "This chat can't be shared" : "Couldn't share this chat",
      body: (blocked && apiDetail(shared.error)) || "Try again.",
    });
    return null;
  }
  if (!copied) {
    window.pushToast({ type: "warning", title: "Copy this link", body: url });
  } else {
    window.pushToast({ type: "success", title: "Link copied", body: "Anyone on the Atlas team can read this chat." });
  }
  return shared.at;
};

// Stop sharing: the link stops opening. Copies readers made stay theirs.
const stopSharing = async (id) => {
  try {
    await window.AtlasApi.assistant.unshare(id);
    window.pushToast({ type: "success", title: "Link turned off" });
    return true;
  } catch (_) {
    window.pushToast({ type: "error", title: "Couldn't turn off the link", body: "Try again." });
    return false;
  }
};

// Earlier threads opened from this record's page (the caller's own), listed
// while the panel is empty. A row reopens its thread; typing starts a new one.
const RECORD_NOUN = { engagement: "deal", company: "company", contact: "contact" };

const RecordHistory = ({ record, activeUser, phone, openingId, onOpen }) => {
  const [state, setState] = React.useState({ status: "loading", rows: [] });
  const load = React.useCallback(async () => {
    setState({ status: "loading", rows: [] });
    try {
      const rows = await window.AtlasApi.assistant.conversations(record);
      setState({ status: "ready", rows: Array.isArray(rows) ? rows : [] });
    } catch (_) {
      setState({ status: "error", rows: [] });
    }
  }, [record.entity_type, record.id]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    load();
  }, [load]);

  const noun = RECORD_NOUN[record.entity_type] || "record";
  const fs = phone ? 14 : 12.5;
  const muted = { fontSize: fs, color: "var(--text-4)", padding: phone ? "4px 2px" : "2px" };
  const who = (r) =>
    activeUser && r.owner_id && r.owner_id === activeUser.id ? "You" : r.owner_name || "Former member";
  const copy = async (r) => {
    if (r.share_blocked) {
      window.pushToast({ type: "info", title: shareBlockedReason(r.share_blocked) });
      return;
    }
    const at = await copyThreadLink(r.id);
    if (at) setState((s) => ({ ...s, rows: s.rows.map((x) => (x.id === r.id ? { ...x, shared_at: at } : x)) }));
  };

  return (
    <section aria-label={`Earlier chats on this ${noun}`} aria-busy={state.status === "loading"}>
      <div
        style={{
          fontSize: phone ? 12 : 10.5,
          fontWeight: 600,
          color: "var(--text-4)",
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          marginBottom: 6,
        }}
      >
        Earlier on this {noun}
      </div>
      {state.status === "loading" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "4px 2px" }}>
          {[62, 48, 55].map((w) => (
            <div key={w} style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <window.Skeleton w={`${w}%`} h={phone ? 14 : 12} />
              <window.Skeleton w={80} h={phone ? 12 : 10} style={{ marginLeft: "auto" }} />
            </div>
          ))}
        </div>
      )}
      {state.status === "error" && (
        <div style={{ ...muted, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: "var(--rose-fg)" }}>Couldn't load earlier chats.</span>
          <button
            type="button"
            onClick={load}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: "var(--accent-text)",
              fontSize: fs,
              padding: 0,
              ...(phone ? { minHeight: 40 } : null),
            }}
          >
            Retry
          </button>
        </div>
      )}
      {state.status === "ready" && state.rows.length === 0 && <div style={muted}>No earlier chats on this {noun}</div>}
      {state.status === "ready" && state.rows.length > 0 && (
        // Negative margin: row text lines up with the label; the hover fill bleeds out.
        <ul style={{ listStyle: "none", margin: "0 -8px", padding: 0, display: "flex", flexDirection: "column", gap: 2 }}>
          {state.rows.map((r) => {
            const opening = openingId === r.id;
            const started = r.created_at ? new Date(r.created_at).toLocaleString() : "";
            const last = r.updated_at ? new Date(r.updated_at).toLocaleString() : "";
            return (
              <li key={r.id} style={{ display: "flex", alignItems: phone ? "center" : "baseline", gap: 2 }}>
                <button
                  type="button"
                  onClick={() => onOpen(r)}
                  disabled={!!openingId}
                  aria-busy={opening}
                  title={phone ? undefined : `Started ${started}${last && last !== started ? ` · last message ${last}` : ""}`}
                  onMouseEnter={(e) => { if (!openingId) e.currentTarget.style.background = "var(--surface-hover)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    // Phone: meta drops under the title so the title keeps the width.
                    flexDirection: phone ? "column" : "row",
                    alignItems: phone ? "stretch" : "baseline",
                    gap: phone ? 2 : 12,
                    textAlign: "left",
                    border: "none",
                    borderRadius: 6,
                    background: "transparent",
                    cursor: openingId ? "default" : "pointer",
                    fontFamily: "inherit",
                    padding: phone ? "8px 8px" : "6px 8px",
                    minHeight: phone ? 48 : undefined,
                    opacity: openingId && !opening ? 0.5 : 1,
                  }}
                >
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: phone ? 15 : 13,
                      color: "var(--text)",
                    }}
                  >
                    {r.title || "Untitled chat"}
                  </span>
                  <span style={{ flexShrink: 0, fontSize: phone ? 13 : 11.5, color: "var(--text-4)", whiteSpace: "nowrap" }}>
                    {opening ? (
                      "Opening…"
                    ) : (
                      <>
                        {r.shared_at && (
                          <span
                            title={phone ? undefined : `Shared by link since ${new Date(r.shared_at).toLocaleString()}`}
                            style={{ color: "var(--accent-text)" }}
                          >
                            Shared ·{" "}
                          </span>
                        )}
                        {`${who(r)} · ${window.relativeTime(r.updated_at || r.created_at)}`}
                      </>
                    )}
                  </span>
                </button>
                {isMine(r.owner_id) && (
                  <button
                    type="button"
                    onClick={() => copy(r)}
                    aria-disabled={r.share_blocked ? "true" : undefined}
                    aria-label={
                      r.share_blocked
                        ? `${shareBlockedReason(r.share_blocked)}: “${r.title || "Untitled chat"}”`
                        : `Copy link to “${r.title || "Untitled chat"}”`
                    }
                    title={r.share_blocked ? shareBlockedReason(r.share_blocked) : "Copy link"}
                    style={{
                      flexShrink: 0,
                      alignSelf: "center",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: phone ? 40 : 24,
                      height: phone ? 40 : 24,
                      border: "none",
                      borderRadius: 6,
                      background: "transparent",
                      color: "var(--text-4)",
                      cursor: r.share_blocked ? "not-allowed" : "pointer",
                      opacity: r.share_blocked ? 0.45 : 1,
                      padding: 0,
                    }}
                    onMouseEnter={(e) => {
                      if (r.share_blocked) return;
                      e.currentTarget.style.background = "var(--surface-hover)";
                      e.currentTarget.style.color = "var(--text-2)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.color = "var(--text-4)";
                    }}
                  >
                    <window.Icon.Copy size={phone ? 15 : 12} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

const AssistantPanel = ({ open, onClose, initialQuery, initialConversationId, context, onOpenView, activeUser }) => {
  const [messages, setMessages] = React.useState([]);
  const [conversationId, setConversationId] = React.useState(null);
  const [input, setInput] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [workStatus, setWorkStatus] = React.useState(""); // live "what Atlas is doing" line
  const [error, setError] = React.useState("");
  const [savedViews, setSavedViews] = React.useState({}); // block id → view id
  const [tasksCreated, setTasksCreated] = React.useState({}); // block id → task count
  const [prefs, setPrefs] = React.useState([]); // what Atlas remembers about me
  const [prefsOpen, setPrefsOpen] = React.useState(false);
  const [openingId, setOpeningId] = React.useState(null); // history row being reopened
  // The open thread's owner + share state ({owner_id, shared_at, share_blocked}).
  const [share, setShare] = React.useState(null);
  const openSeq = React.useRef(0); // bumps when the user moves on; a stale reopen is dropped
  const record = context && context.entity_type && context.id ? context : null;
  const bottomRef = React.useRef(null);
  const sentInitial = React.useRef(null);
  const openedInitial = React.useRef(null);
  // Phone: the panel is a full-screen sheet (window.Drawer). The composer
  // moves into the sheet's footer, which rides above the on-screen keyboard,
  // and its textarea grows with the question.
  const device = window.useDevice ? window.useDevice() : window.AtlasDevice || {};
  const phone = !!device.phone;
  const inputRef = React.useRef(null);
  const navWatch = React.useRef(0);
  React.useLayoutEffect(() => {
    const ta = inputRef.current;
    if (!phone || !ta) return;
    ta.style.height = "auto";
    const sh = ta.scrollHeight; // 0 = not laid out yet: keep the CSS height
    ta.style.height = sh ? `${Math.min(sh + 2, 140)}px` : "";
  }, [input, phone, open]);
  React.useEffect(() => () => clearInterval(navWatch.current), []);

  const loadPrefs = React.useCallback(async () => {
    try {
      setPrefs((await window.AtlasApi.assistant.preferences()) || []);
    } catch (_) {}
  }, []);
  React.useEffect(() => {
    if (open) loadPrefs();
  }, [open, loadPrefs]);

  const send = React.useCallback(
    async (text, convId) => {
      const trimmed = (text || "").trim();
      if (!trimmed) return;
      setError("");
      setSending(true);
      setWorkStatus("Thinking…");
      setMessages((ms) => [...ms, { role: "user", content: trimmed }]);
      try {
        const r = await window.AtlasApi.assistant.chatStream(
          {
            message: trimmed,
            conversation_id: convId || undefined,
            context: context || undefined,
          },
          (label) => setWorkStatus(label || "Thinking…")
        );
        // A reply with no message (an unexpected 200 shape) shows the error
        // line; an undefined entry in the thread would blank the whole app.
        if (!r || !r.message) throw new Error("The assistant is unavailable right now.");
        setConversationId(r.conversation_id || convId || null);
        // A turn that read something private makes the thread unshareable
        // (and the server takes any link down).
        setShare((s) => {
          const base = convId && s ? s : { owner_id: (activeUser && activeUser.id) || null, shared_at: null };
          return r.share_blocked ? { ...base, shared_at: null, share_blocked: r.share_blocked } : base;
        });
        setMessages((ms) => [...ms, r.message]);
        if (r.saved_preferences && r.saved_preferences.length) {
          // Saving is never silent — surface what Atlas will remember.
          r.saved_preferences.forEach((n) =>
            window.pushToast({ type: "info", title: "Atlas will remember", body: n })
          );
          loadPrefs();
        }
        if (r.created_tasks && r.created_tasks.length) {
          // A write is never silent either — say what landed and for whom.
          r.created_tasks.forEach((t) =>
            window.pushToast({
              type: "success",
              title: "Task created",
              body: `${t.title} · due ${t.due_date}${t.assignee_name ? ` · ${t.assignee_name}` : ""}`,
            })
          );
        }
      } catch (e) {
        setError(e && e.message ? e.message : "The assistant is unavailable right now.");
      } finally {
        setSending(false);
        setWorkStatus("");
      }
    },
    [context, loadPrefs, activeUser]
  );

  // A query handed over from ⌘K fires once per open.
  React.useEffect(() => {
    if (open && initialQuery && sentInitial.current !== initialQuery) {
      sentInitial.current = initialQuery;
      setMessages([]);
      setConversationId(null);
      setShare(null);
      send(initialQuery, null);
    }
    if (!open) sentInitial.current = null;
  }, [open, initialQuery, send]);

  React.useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, sending]);

  const onSaveView = async (block) => {
    try {
      const v = await window.AtlasApi.views.create({
        name: block.title || "Ask Atlas results",
        source_entity: block.entity || "companies",
        spec: block.spec,
        visibility: "private",
      });
      setSavedViews((s) => ({ ...s, [block.id]: v.id }));
      window.pushToast({ type: "success", title: "View saved", body: block.title });
      if (onOpenView && v && v.id) onOpenView(v.id);
    } catch (e) {
      window.pushToast({
        type: "error",
        title: "Save failed",
        body: e && e.message ? e.message : "",
      });
    }
  };

  // One follow-up task per company row, assigned to the current user. Runs
  // only from the block's "Create tasks" button — never from the model.
  const onCreateTasks = async (block, days) => {
    const dueAt = new Date(); dueAt.setDate(dueAt.getDate() + days);
    const due = window.localISODate(dueAt);
    const rows = (block.rows || []).filter((r) => r.__id).slice(0, 50);
    const results = await Promise.allSettled(
      rows.map((r) =>
        window.AtlasApi.createTask({
          title: `Follow up with ${r.name}`,
          company_id: r.__id,
          assigned_to: activeUser.id,
          created_by: activeUser.id,
          task_type: "follow_up",
          due_date: due,
          notes: `From Ask Atlas: ${block.title || "results"}`,
        })
      )
    );
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const failed = rows.length - ok;
    setTasksCreated((s) => ({ ...s, [block.id]: ok }));
    window.dispatchEvent(new CustomEvent("task-created"));
    window.pushToast({
      type: failed ? "warning" : "success",
      title: `${ok} task${ok === 1 ? "" : "s"} created`,
      body: failed ? `${failed} failed — check the Tasks page.` : `Due ${due}, assigned to you.`,
    });
  };

  const newChat = () => {
    openSeq.current += 1;
    setOpeningId(null);
    setMessages([]);
    setConversationId(null);
    setShare(null);
    setError("");
  };

  const openThread = async (row) => {
    const seq = ++openSeq.current;
    setError("");
    setOpeningId(row.id);
    try {
      const t = await window.AtlasApi.assistant.conversation(row.id);
      if (seq !== openSeq.current) return;
      setMessages(((t && t.messages) || []).filter(Boolean));
      setConversationId(row.id);
      setShare(t ? { owner_id: t.owner_id, shared_at: t.shared_at || null, share_blocked: t.share_blocked || null } : null);
    } catch (e) {
      if (seq !== openSeq.current) return;
      setError(e && e.message ? e.message : "Couldn't open that chat.");
    } finally {
      if (seq === openSeq.current) setOpeningId(null);
    }
  };

  // A thread handed over by id (a shared link's "Continue in my own chat")
  // opens once per hand-over.
  React.useEffect(() => {
    if (open && initialConversationId && openedInitial.current !== initialConversationId) {
      openedInitial.current = initialConversationId;
      openThread({ id: initialConversationId });
    }
    if (!open) openedInitial.current = null;
  }, [open, initialConversationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // While a past thread is loading, nothing can be sent — a question typed
  // then would start a new thread that the reopened one then replaces.
  const busy = sending || !!openingId;

  const submit = () => {
    if (busy) return;
    const t = input;
    setInput("");
    send(t, conversationId);
  };

  // Phone: a record link in an answer (or a result table's name cell)
  // navigates underneath a full-screen sheet, so nothing would visibly
  // happen. After a tap in the thread, close the sheet once the route
  // actually changes — a buyer link resolves async, hence the short watch.
  const watchNav = () => {
    const before = window.location.hash;
    const started = Date.now();
    clearInterval(navWatch.current);
    navWatch.current = setInterval(() => {
      if (window.location.hash !== before) {
        clearInterval(navWatch.current);
        onClose();
      } else if (Date.now() - started > 2500) {
        clearInterval(navWatch.current);
      }
    }, 80);
  };

  if (!open) return null;

  const forget = async (p) => {
    try {
      await window.AtlasApi.assistant.removePreference(p.id);
      setPrefs((xs) => xs.filter((x) => x.id !== p.id));
      window.pushToast({ type: "success", title: "Forgotten" });
    } catch (e) {
      window.pushToast({
        type: "error",
        title: "Delete failed",
        body: e && e.message ? e.message : "",
      });
    }
  };

  // "New chat · Atlas remembers N · $ today" — under the input on both.
  // Phone: finger-sized text buttons, and no title= (the label says it).
  const linkBtn = phone ? { minHeight: 40, padding: "0 2px", fontSize: 14 } : null;
  const metaRow = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginTop: 6,
        flexWrap: "wrap",
        fontSize: 11,
        color: "var(--text-4)",
        ...(phone ? { gap: 16, marginTop: 2, fontSize: 13 } : null),
      }}
    >
      <button
        type="button"
        onClick={newChat}
        style={{
          border: "none",
          background: "transparent",
          cursor: "pointer",
          fontSize: 11,
          color: "var(--accent-text)",
          padding: 0,
          ...linkBtn,
        }}
      >
        New chat
      </button>
      {conversationId && !busy && share && isMine(share.owner_id) && (
        <>
          {share.shared_at && !share.share_blocked && (
            <span
              title={phone ? undefined : `Shared by link since ${new Date(share.shared_at).toLocaleString()}`}
              style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--text-3)" }}
            >
              <window.Icon.Globe size={phone ? 13 : 10} />
              Shared
            </span>
          )}
          <button
            type="button"
            aria-disabled={share.share_blocked ? "true" : undefined}
            onClick={async () => {
              if (share.share_blocked) {
                window.pushToast({ type: "info", title: shareBlockedReason(share.share_blocked) });
                return;
              }
              const cur = share;
              const at = await copyThreadLink(conversationId);
              if (at) setShare((s) => (s === cur ? { ...s, shared_at: at } : s));
            }}
            title={
              share.share_blocked
                ? shareBlockedReason(share.share_blocked)
                : phone
                ? undefined
                : "Copy a link anyone on the Atlas team can open"
            }
            style={{
              border: "none",
              background: "transparent",
              cursor: share.share_blocked ? "not-allowed" : "pointer",
              fontSize: 11,
              color: share.share_blocked ? "var(--text-4)" : "var(--accent-text)",
              padding: 0,
              ...linkBtn,
            }}
          >
            Copy link
          </button>
          {share.shared_at && !share.share_blocked && (
            <button
              type="button"
              onClick={async () => {
                const cur = share;
                if (await stopSharing(conversationId)) setShare((s) => (s === cur ? { ...s, shared_at: null } : s));
              }}
              title={phone ? undefined : "Turn off this chat's link"}
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                fontSize: 11,
                color: "var(--text-3)",
                padding: 0,
                ...linkBtn,
              }}
            >
              Stop sharing
            </button>
          )}
        </>
      )}
      {prefs.length > 0 && (
        <button
          onClick={() => setPrefsOpen((v) => !v)}
          title={phone ? undefined : "Preferences Atlas has learned from you — click to review or remove"}
          style={{
            border: "none",
            background: "transparent",
            cursor: "pointer",
            fontSize: 11,
            color: "var(--text-3)",
            padding: 0,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            ...linkBtn,
          }}
        >
          <window.Icon.Sparkle size={phone ? 12 : 10} /> Atlas remembers {prefs.length}
        </button>
      )}
    </div>
  );

  const prefsPanel = prefsOpen && prefs.length > 0 && (
    <div
      style={{
        marginTop: 8,
        border: "1px solid var(--border)",
        borderRadius: 8,
        padding: "8px 10px",
        background: "var(--surface-2)",
        display: "flex",
        flexDirection: "column",
        gap: 6,
        // Phone: it opens above the input inside the sheet footer — cap it
        // so the keyboard and the thread keep their room.
        ...(phone ? { marginTop: 0, marginBottom: 8, maxHeight: "32vh", overflowY: "auto" } : null),
      }}
    >
      <div
        style={{
          fontSize: phone ? 12 : 10.5,
          fontWeight: 600,
          color: "var(--text-4)",
          letterSpacing: "0.05em",
          textTransform: "uppercase",
        }}
      >
        What Atlas remembers about you
      </div>
      {prefs.map((p) => (
        <div key={p.id} style={{ display: "flex", alignItems: phone ? "center" : "flex-start", gap: 8 }}>
          <span style={{ flex: 1, fontSize: phone ? 14 : 12, color: "var(--text-2)", lineHeight: 1.4 }}>
            {p.content}
          </span>
          <button
            onClick={() => forget(p)}
            title={phone ? undefined : "Forget this"}
            aria-label={phone ? `Forget: ${p.content}` : undefined}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: "var(--text-3)",
              fontSize: 13,
              lineHeight: 1,
              padding: 0,
              ...(phone ? { width: 40, height: 40, fontSize: 20, flexShrink: 0 } : null),
            }}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );

  const onInputKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  // Phone composer — the Drawer sheet's footer, which already sits above the
  // home indicator and the keyboard (padding max(--sab, --kb)). 16px input
  // (no iOS focus zoom), a round Send, the keyboard's return key says "send".
  const phoneComposer = phone && (
    <div style={{ flex: "1 1 100%", minWidth: 0 }}>
      {prefsPanel}
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onInputKey}
          placeholder={sending ? "Atlas is working…" : messages.length ? "Ask a follow-up…" : "Ask Atlas…"}
          rows={1}
          disabled={busy}
          enterKeyHint="send"
          style={{
            flex: 1,
            minWidth: 0,
            resize: "none",
            fontSize: 16,
            lineHeight: 1.4,
            minHeight: 44,
            maxHeight: 140,
            overflowY: "auto",
            fontFamily: "inherit",
            color: "var(--text)",
            background: "var(--surface-2)",
            border: "1px solid var(--border)",
            borderRadius: 22,
            padding: "10px 14px",
            outline: "none",
          }}
        />
        <button
          type="button"
          aria-label="Send"
          onClick={submit}
          disabled={busy || !input.trim()}
          style={{
            width: 44,
            height: 44,
            flexShrink: 0,
            borderRadius: 999,
            border: "none",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: "var(--accent)",
            color: "#fff",
            opacity: busy || !input.trim() ? 0.4 : 1,
            WebkitTapHighlightColor: "transparent",
            touchAction: "manipulation",
          }}
        >
          <window.Icon.ArrowUp size={18} />
        </button>
      </div>
      {metaRow}
    </div>
  );

  // Phone empty state: the desktop's examples, one tap away instead of a
  // list to retype on a phone keyboard.
  const EXAMPLES = [
    "Find 5 HVAC companies we haven't reached out to",
    "Untouched prospects in GA or FL with EBITDA over $1M",
    "Good fits for the Acme deal that never got a call",
    "Which buyers should we send the Acme deal to?",
  ];

  return (
    <window.Drawer
      open={open}
      onClose={onClose}
      title={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
          <window.Icon.Sparkle size={14} /> Ask Atlas
        </span>
      }
      width={640}
      footer={phone ? phoneComposer : null}
    >
      <div
        style={{ display: "flex", flexDirection: "column", gap: 14, minHeight: "100%" }}
        onClickCapture={phone ? watchNav : undefined}
      >
        {record && messages.length === 0 && !sending && (
          <window.ErrorBoundary name="ask-atlas-history" resetKey={`${record.entity_type}:${record.id}`} fallback={null}>
            <RecordHistory
              key={`${record.entity_type}:${record.id}`}
              record={record}
              activeUser={activeUser}
              phone={phone}
              openingId={openingId}
              onOpen={openThread}
            />
          </window.ErrorBoundary>
        )}
        {messages.length === 0 && !sending && !phone && (
          <div
            style={{ fontSize: 12.5, color: "var(--text-3)", lineHeight: 1.6, padding: "8px 2px" }}
          >
            Try things like:
            <ul style={{ margin: "6px 0 0 16px", padding: 0 }}>
              <li>"Find 5 HVAC companies we haven't reached out to"</li>
              <li>"Untouched prospects in GA or FL with EBITDA over $1M"</li>
              <li>"Good fits for the Acme deal that never got a call"</li>
              <li>"Which buyers should we send the Acme deal to?"</li>
            </ul>
          </div>
        )}
        {messages.length === 0 && !sending && phone && (
          <div>
            <div style={{ fontSize: 14, color: "var(--text-3)", marginBottom: 8 }}>Try asking:</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {EXAMPLES.map((x) => (
                <button
                  key={x}
                  type="button"
                  onClick={() => send(x, conversationId)}
                  disabled={busy}
                  style={{
                    textAlign: "left",
                    minHeight: 48,
                    padding: "10px 14px",
                    borderRadius: 12,
                    border: "1px solid var(--border)",
                    background: "var(--surface-2)",
                    color: "var(--text)",
                    fontSize: 15,
                    lineHeight: 1.35,
                    fontFamily: "inherit",
                    cursor: "pointer",
                  }}
                >
                  {x}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.filter(Boolean).map((m, i) => (
          <AssistantMsg
            key={m.id || i}
            msg={m}
            onSaveView={onSaveView}
            savedViews={savedViews}
            activeUser={activeUser}
            tasksCreated={tasksCreated}
            onCreateTasks={onCreateTasks}
            phone={phone}
          />
        ))}
        {sending && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontSize: phone ? 14 : 12.5,
              color: "var(--text-3)",
            }}
          >
            <style>{`
              @keyframes atlas-ai-pulse { 0%, 100% { opacity: 0.4; } 50% { opacity: 1; } }
              @keyframes atlas-ai-status-in { from { opacity: 0; transform: translateY(3px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
            <span style={{ display: "inline-flex", animation: "atlas-ai-pulse 1.6s ease-in-out infinite" }}>
              <window.Icon.Sparkle size={12} />
            </span>
            {/* key remounts the label so each new step fades in */}
            <span key={workStatus} style={{ animation: "atlas-ai-status-in 0.25s ease" }}>
              {workStatus || "Atlas is working…"}
            </span>
          </div>
        )}
        {error && <div style={{ fontSize: phone ? 14 : 12.5, color: "var(--rose-fg)" }}>{error}</div>}
        <div ref={bottomRef} />

        {!phone && (
          <div
            style={{
              marginTop: "auto",
              position: "sticky",
              bottom: 0,
              background: "var(--surface)",
              paddingTop: 8,
            }}
          >
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onInputKey}
                placeholder={sending ? "Atlas is working…" : "Ask a follow-up… (↵ to send)"}
                rows={2}
                disabled={busy}
                style={{
                  flex: 1,
                  resize: "none",
                  fontSize: 13,
                  fontFamily: "inherit",
                  color: "var(--text)",
                  background: "var(--surface-2)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  padding: "8px 10px",
                  outline: "none",
                }}
              />
              <window.Btn size="sm" onClick={submit} disabled={busy || !input.trim()}>
                Send
              </window.Btn>
            </div>
            {metaRow}
            {prefsPanel}
          </div>
        )}
      </div>
    </window.Drawer>
  );
};

// A thread opened by its share link (#/ask/<id>): read-only for everyone but
// its owner. "Continue in my own chat" copies it into a new thread of the
// reader's own (POST /assistant/shared/<id>/continue) and opens that in Ask
// Atlas; the owner just reopens theirs.
const SharedAskThread = ({ threadId, activeUser, phone }) => {
  const [state, setState] = React.useState({ status: "loading", thread: null });
  const [continuing, setContinuing] = React.useState(false);
  const load = React.useCallback(async () => {
    setState({ status: "loading", thread: null });
    try {
      const t = await window.AtlasApi.assistant.shared(threadId);
      if (!t || !t.id) throw Object.assign(new Error("Conversation not found"), { status: 404 });
      setState({ status: "ready", thread: t });
    } catch (e) {
      // 404: never shared, deleted, or no such thread. 403: a role without
      // Ask Atlas. 422: not a thread id at all (a mangled link).
      const missing = e && (e.status === 404 || e.status === 403 || e.status === 422);
      setState({ status: missing ? "missing" : "error", thread: null });
    }
  }, [threadId]);
  React.useEffect(() => {
    load();
  }, [load]);
  // Focus lands on what loaded — the title, or the message saying why not.
  const focusRef = React.useRef(null);
  React.useEffect(() => {
    if (state.status !== "loading" && focusRef.current) focusRef.current.focus({ preventScroll: true });
  }, [state.status]);

  const t = state.thread;
  const mine = !!(t && t.is_owner);
  const owner = t ? (mine ? "You" : t.owner_name || "Former member") : "";
  const openInAsk = async () => {
    if (mine) {
      window.dispatchEvent(new CustomEvent("ask-atlas", { detail: { conversationId: t.id } }));
      return;
    }
    setContinuing(true);
    try {
      const c = await window.AtlasApi.assistant.continueShared(t.id);
      window.dispatchEvent(new CustomEvent("ask-atlas", { detail: { conversationId: c.id } }));
    } catch (e) {
      const gone = e && (e.status === 404 || e.status === 422);
      window.pushToast({
        type: "error",
        title: "Couldn't start your chat",
        body: gone ? "This chat isn't shared anymore." : "Try again in a moment.",
      });
      if (gone) load();
    } finally {
      setContinuing(false);
    }
  };

  const muted = { fontSize: phone ? 14 : 12.5, color: "var(--text-3)" };
  return (
    <div style={{ height: "100%", width: "100%", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
      <div
        style={{
          width: "100%",
          maxWidth: 760,
          margin: "0 auto",
          padding: phone ? "14px 16px 40px" : "22px 24px 56px",
          boxSizing: "border-box",
        }}
      >
        {state.status === "loading" && (
          <div aria-busy="true" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <window.Skeleton w="70%" h={phone ? 20 : 22} />
            <window.Skeleton w={180} h={12} />
            <div style={{ height: 12 }} />
            {[58, 92, 76].map((w, i) => (
              <window.Skeleton key={i} w={`${w}%`} h={i % 2 ? 54 : 30} style={i % 2 ? null : { alignSelf: "flex-end" }} />
            ))}
          </div>
        )}
        {state.status === "missing" && (
          <div role="status" style={{ paddingTop: 24, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 12 }}>
            <h1
              ref={focusRef}
              tabIndex={-1}
              style={{ fontSize: phone ? 16 : 14, fontWeight: 600, color: "var(--text)", margin: 0, outline: "none" }}
            >
              This chat isn't shared, or it was deleted
            </h1>
            <window.Btn variant="outline" size="sm" onClick={() => { window.location.hash = "#/"; }}>
              Go to Home
            </window.Btn>
          </div>
        )}
        {state.status === "error" && (
          <div role="alert" style={{ paddingTop: 24, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span ref={focusRef} tabIndex={-1} style={{ ...muted, color: "var(--rose-fg)", outline: "none" }}>
              Couldn't load this chat.
            </span>
            <window.Btn variant="outline" size="sm" icon={<window.Icon.Refresh size={11} />} onClick={load}>
              Retry
            </window.Btn>
          </div>
        )}
        {state.status === "ready" && (
          <>
            <header
              style={{
                display: "flex",
                alignItems: phone ? "stretch" : "flex-start",
                flexDirection: phone ? "column" : "row",
                gap: phone ? 12 : 16,
                paddingBottom: 14,
                borderBottom: "1px solid var(--border)",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <h1
                  ref={focusRef}
                  tabIndex={-1}
                  style={{
                    outline: "none",
                    fontSize: phone ? 19 : 20,
                    fontWeight: 650,
                    margin: 0,
                    color: "var(--text)",
                    fontFamily: "var(--font-display)",
                    letterSpacing: "-0.01em",
                    lineHeight: 1.3,
                    overflowWrap: "anywhere",
                  }}
                >
                  {t.title || "Untitled chat"}
                </h1>
                <div style={{ ...muted, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
                  <span title={t.created_at ? new Date(t.created_at).toLocaleString() : undefined}>
                    {owner} · {window.relativeTime(t.updated_at || t.created_at)}
                  </span>
                  {!mine && (
                    <window.Pill sz="sm" title={`Only ${t.owner_name || "its owner"} can add to this chat`}>
                      Read-only
                    </window.Pill>
                  )}
                </div>
              </div>
              <window.Btn
                variant="primary"
                size={phone ? "md" : "sm"}
                icon={<window.Icon.Sparkle size={11} />}
                onClick={openInAsk}
                disabled={continuing}
                title={mine ? undefined : "Starts a new chat of your own with this one's messages"}
              >
                {mine ? "Open in Ask Atlas" : continuing ? "Starting…" : "Continue in my own chat"}
              </window.Btn>
            </header>
            <div style={{ display: "flex", flexDirection: "column", gap: phone ? 18 : 16, paddingTop: 18 }}>
              {(t.messages || []).filter(Boolean).map((m, i) => (
                <AssistantMsg
                  key={m.id || i}
                  msg={m}
                  readOnly
                  phone={phone}
                  activeUser={activeUser}
                  savedViews={{}}
                  tasksCreated={{}}
                />
              ))}
              {(t.messages || []).length === 0 && <div style={muted}>No messages yet</div>}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

window.AssistantPanel = AssistantPanel;
window.SharedAskThread = SharedAskThread;
